<script setup lang="ts">
import type { StringMap } from '@/types/common';
import dayjs from 'dayjs';
import { isObject } from 'lodash';

const LIMIT_SCROLL_HEIGHT_DESKTOP = 520; // Landing's page banner height(600) - header's height(80)
const LIMIT_SCROLL_HEIGHT_MOBILE = 420; // Landing's page banner height(500) - header's height(80)

const layoutStore = useLayoutStore();
const authStore = useAuthStore();
const { isMobile, isTablet } = useWindowResize();
const profileStore = useProfileStore();
const route = useRoute();

const currentYear = dayjs().year();

const isScrolling = ref(false);
const showConfirmSwitchUiDialog = ref(false);

const isAccessible = computed(() => layoutStore.isGeolocationChecked);
const isHomePage = computed(() => route.name === ROUTE.INDEX.NAME);
const profileCountry = computed(() => profileStore.profile?.countryId);

const isInformationPage = computed(() => {
	const pages = [
		ROUTE.INDEX.NAME,
		ROUTE.PRICING.NAME,
		ROUTE.ABOUT_US.NAME,
	];
	return pages.includes(route.name?.toString() || '');
});

const isShowHeaderInTransparentBackground = computed(() => isHomePage.value && !isScrolling.value);
const isShowFullScreen = computed(() => layoutStore.isFullScreen);

function handleScroll() {
	if (!window.top) {
		return;
	}

	const homePageLimitHeight = isMobile.value ? LIMIT_SCROLL_HEIGHT_MOBILE : LIMIT_SCROLL_HEIGHT_DESKTOP;
	const defaultLimitHeight = 1;
	const limitHeight = isHomePage.value ? homePageLimitHeight : defaultLimitHeight;

	if (window.top.scrollY > limitHeight && !isScrolling.value) {
		isScrolling.value = true;
	} else if (window.top.scrollY < limitHeight && isScrolling.value) {
		isScrolling.value = false;
	}
}

function handleConfirmSwitchUI() {
	// Navigate to old peatix website without sign out
	authStore.proceedSecondSignIn();
}

function captureUtmParams() {
	const storedUtmValues = getSessionStorage(SESSION_STORAGE_ITEMS.UTM);
	const utmValues: StringMap = isObject(storedUtmValues) ? storedUtmValues as any : {};

	const newUtmValues: StringMap = {
		utm_source: getQueryParam('utm_source'),
		utm_medium: getQueryParam('utm_medium'),
		utm_campaign: getQueryParam('utm_campaign'),
		utm_term: getQueryParam('utm_term'),
	};

	// Check if there are any valid UTM values
	const hasValidUtmValues = Object.values(newUtmValues).some((value) => value !== null && value !== '');

	// Stop function execution if no valid UTM values
	if (!hasValidUtmValues) {
		return;
	}

	// Update the utmValues object with the new values, but only if the new value is not an empty string
	// This ensures that we preserve the previous UTM values as long as possible until the session ends or a new value is provided
	let isUtmUpdated = false;
	Object.keys(newUtmValues).forEach((key) => {
		if (newUtmValues[key]) {
			utmValues[key] = newUtmValues[key];
			isUtmUpdated = true;
		}
	});

	// Remove any empty values from the utmValues object
	Object.keys(utmValues).forEach((key) => {
		if (utmValues[key] === '') {
			delete utmValues[key];
		}
	});

	if (isUtmUpdated) {
		setSessionStorage(SESSION_STORAGE_ITEMS.UTM, JSON.stringify(utmValues));
	}
}

watch(profileCountry, async (countryId) => {
	if (!profileStore.profileId) {
		return;
	}

	// Show confirm switch UI dialog if country ID is not US
	if (countryId && countryId !== DEFAULT_COUNTRY) {
		showConfirmSwitchUiDialog.value = true;
	} else if (countryId && countryId === DEFAULT_COUNTRY && authStore.isReadyForSecondSignIn) {
		authStore.proceedSecondSignIn();
	}
});

onMounted(() => {
	window.addEventListener('scroll', handleScroll);

	// Capture UTM values and store them in session storage if there are any parameters in the URL
	if (new URL(window.location.href).searchParams.toString() !== '') {
		captureUtmParams();
	}
});

onUnmounted(() => {
	window.removeEventListener('scroll', handleScroll);
});
</script>

<template>
  <div
    v-if="isAccessible"
    :class="{'page-action-bar' : layoutStore.hasActionBar }"
  >
    <header
      v-if="!isShowFullScreen"
      :class="{
        'home': isHomePage,
        'transparent': isShowHeaderInTransparentBackground,
        'scrolling': isScrolling }"
    >
      <div class="search-container">
        <RouterLink :to="ROUTE.INDEX.PATH">
          <Logo :is-transparent-background="isShowHeaderInTransparentBackground" />
        </RouterLink>
        <EventSearchBox v-if="!isTablet" />
      </div>
      <div class="menu-container">
        <EventSearchBox
          v-if="isTablet"
          :is-transparent-background="isShowHeaderInTransparentBackground"
          is-tablet
        />
        <Menu :is-transparent-background="isShowHeaderInTransparentBackground" />
      </div>
    </header>
    <main :class="{'information' : isInformationPage}">
      <BaseNotificationBox />
      <RouterView />
    </main>
    <footer v-if="!isShowFullScreen">
      <FooterLink />
      <span class="copyright">© {{ currentYear }} Peatix</span>
    </footer>
    <ConfirmDialog
      :is-show="showConfirmSwitchUiDialog"
      confirm-button-name="Ok, I got it"
      hide-cancel-button
      hide-close-icon
      @on-confirm="handleConfirmSwitchUI"
    >
      <template #header>
        <AlertCircleIcon
          class="icon-header-confirm-dlg icon-info"
          :color="ICON_COLOR.PRIMARY"
        />
      </template>
      <template #title>
        Update your country setting
      </template>
      <template #content>
        <div class="content">
          <p class="text-sm text-regular">
            To view this page, please update your country setting in your account settings to the United States.
          </p>
        </div>
      </template>
    </ConfirmDialog>
  </div>
</template>

<style lang="scss" scoped>
$headerHeight: rem(80);
$footerHeightDesktop: rem(120);
$footerHeightMobile: rem(288);

.page-action-bar {
  // Add margin bottom if page has action bar
  margin-bottom: rem(62);

  @include media-query-max(mobile) {
    margin-bottom: rem(102);
  }
}

header, footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
}

header {
  position: sticky;
  top: calc(env(safe-area-inset-top, 0) + 0);
  padding: spacings-get(3) spacings-get(8);
  height: $headerHeight;
  background-color: colors-get(base, white);
  z-index: 3;

  // Transition
  -webkit-transition: all 0.5s ease-out;
  -moz-transition: all 0.5s ease-out;
  -o-transition: all 0.5s ease-out;
  transition: all 0.5s ease-out;

  &.home {
    position: fixed;
    width: 100%;
  }

  &.transparent {
    background-color: transparent;
  }

  &.scrolling {
    position: sticky;
    top: 0;
    box-shadow: 0px 1px 1px 0px rgba(16, 24, 40, 0.05);
  }

  .search-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: spacings-get(6);
  }

  .menu-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: spacings-get(4);
  }
}

footer {
  padding: 0 spacings-get(8);
  height: $footerHeightDesktop;
}

main {
  min-height: calc(100vh - ($headerHeight + $footerHeightDesktop)); // header + footer
  margin: auto;

  &:not(.information) {
    max-width: 1440px;
    padding: 0 spacings-get(4);

    @include media-query-max(mobile) {
      padding: 0;
    }
  }

  @include media-query-max(tablet) {
    min-height: calc(100vh - ($headerHeight + $footerHeightMobile)); // header + footer
  }
}

footer {
  .copyright {
    @include fonts-get(semibold, text-md);
    color: colors-get(gray, 500);
  }
}

@include media-query-max(tablet) {
  header {
    padding: spacings-get(3) spacings-get(4);
  }

  footer {
    flex-direction: column;
    align-items: center;
    row-gap: rem(18);
    padding: spacings-get(12) 0;
    height: auto;
  }
}
</style>
